import { useCallback, useEffect, useReducer, useRef } from 'react';
import { companyInvoiceSummaryService as defaultService } from '../services/companyInvoiceSummaryService';

// ─── State Machine ────────────────────────────────────────────────────────────

const INITIAL_STATE = { status: 'idle', data: null, error: null };

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, status: 'loading', error: null };
    case 'REFRESH_START':
      return { ...state, status: 'refreshing', error: null };
    case 'FETCH_SUCCESS':
      return { status: 'success', data: action.payload, error: null };
    case 'FETCH_ERROR':
      return { ...state, status: 'error', error: action.payload };
    default:
      return { ...state };
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Low-level composable hook. Executes company_invoice_summary whenever `params`
 * reference changes. Callers are responsible for memoizing `params` so that
 * only meaningful date range changes trigger a new request.
 *
 * `service` is injected for testability — pass a mock in tests.
 */
export function useGetCompanyInvoiceSummary(params, service = defaultService) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // Once a successful load has occurred, subsequent re-fetches dispatch
  // REFRESH_START so existing data stays visible while the new request is in flight.
  const hasLoadedOnce = useRef(false);
  const requestId = useRef(0);

  const execute = useCallback(
    async forceRefresh => {
      // eslint-disable-next-line no-plusplus
      const currentId = ++requestId.current;
      dispatch({
        type:
          forceRefresh || hasLoadedOnce.current
            ? 'REFRESH_START'
            : 'FETCH_START',
      });
      try {
        const summary = await service.getCompanyInvoiceSummary(params);
        if (currentId !== requestId.current) return;
        hasLoadedOnce.current = true;
        dispatch({ type: 'FETCH_SUCCESS', payload: summary });
      } catch (err) {
        if (currentId !== requestId.current) return;
        dispatch({ type: 'FETCH_ERROR', payload: err });
      }
    },
    [service, params],
  );

  useEffect(() => {
    execute(false);
  }, [execute]);

  const refresh = useCallback(() => execute(true), [execute]);

  return {
    status: state.status,
    summary: state.data,
    error: state.error,
    refresh,
  };
}

export default {};
